import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { formatDistanceToNow } from "date-fns";
import { Archive, Users } from "lucide-react";

import type { Group } from "@cohesive/api-entities";
import {
  BaseEntityCard,
  BaseEntityCardActions,
  BaseEntityCardBody,
  BaseEntityCardContent,
  BaseEntityCardContextMenu,
  BaseEntityCardDescription,
  BaseEntityCardExternalLinkAction,
  BaseEntityCardHeader,
  BaseEntityCardIcon,
  BaseEntityCardMentionAction,
  BaseEntityCardMetadata,
  BaseEntityCardProps,
  BaseEntityCardTitle,
} from "../BaseCard";

export { Group };

type GroupCardProps = Group &
  BaseEntityCardProps & {
    // Notion MCP specific fields
    archived?: boolean;
    role?: string;
  };

// Avatar component for GroupCard
export function GroupCardAvatar({ className, style }: Partial<GroupCardProps>) {
  return (
    <BaseEntityCardIcon className={className} style={style}>
      <Users className="h-5 w-5 text-muted-foreground" />
    </BaseEntityCardIcon>
  );
}

export function GroupCard({
  onClick,
  onDoubleClick,
  onMentionClick,
  actions,
  processes,
  className,
  style,
  updated_at,
  archived,
  role,
  ...groupProps
}: GroupCardProps) {
  return (
    <BaseEntityCard
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      processes={processes}
      className={cn(className, { "opacity-60": archived })}
      style={style}
    >
      <GroupCardAvatar />

      <BaseEntityCardContent>
        <BaseEntityCardHeader>
          <BaseEntityCardTitle platform={groupProps.platform}>
            {groupProps.name}
            {archived && (
              <Archive className="h-4 w-4 text-muted-foreground inline ml-2" />
            )}
          </BaseEntityCardTitle>
          <BaseEntityCardActions>
            {groupProps.web_link && (
              <BaseEntityCardExternalLinkAction
                href={groupProps.web_link}
                tooltip="Open team"
              />
            )}
            {onMentionClick && (
              <BaseEntityCardMentionAction onClick={onMentionClick} />
            )}
            {actions && actions.length > 0 && (
              <BaseEntityCardContextMenu actions={actions} />
            )}
          </BaseEntityCardActions>
        </BaseEntityCardHeader>

        <BaseEntityCardBody>
          {groupProps.description && (
            <BaseEntityCardDescription className="line-clamp-2">
              {groupProps.description}
            </BaseEntityCardDescription>
          )}
          <BaseEntityCardMetadata>
            {role && (
              <span className="capitalize">
                {role === "member" ? "👤 Member" : role}
              </span>
            )}
            {updated_at && (
              <span>
                Updated{" "}
                {formatDistanceToNow(new Date(updated_at), {
                  addSuffix: true,
                })}
              </span>
            )}
          </BaseEntityCardMetadata>
        </BaseEntityCardBody>
      </BaseEntityCardContent>
    </BaseEntityCard>
  );
}

export function GroupCardSkeleton({
  className,
  style,
}: {
  className?: string;
  style?: React.CSSProperties;
}) {
  return (
    <BaseEntityCard className={className} style={style}>
      <Skeleton className="h-10 w-10 rounded-full" />
      <BaseEntityCardContent>
        <Skeleton className="h-4 w-1/3 mt-1.5 mb-2.5" />
        <Skeleton className="h-3 w-1/4 mb-1.5" />
      </BaseEntityCardContent>
    </BaseEntityCard>
  );
}
